<!--
  --
  -- 免許証情報設定 グループ
  --
  -->
<template>
  <c-form card-style
    v-model="valid"
    :errors="errors"
    @submit="onSubmit"
  >
    <!-- Title -->
    <v-card-title>
      <slot name="title"/>
    </v-card-title>

    <!-- Contents -->
    <v-card-text>
      <slot/>

      <!-- 国籍 -->
      <v-radio-group
        label="国籍"
        v-model="form.country"
        @change="onChangeCountry"
      >
        <v-radio label="日本" :value=1 :key=1></v-radio>
        <v-radio label="外国籍" :value=2 :key=2 :disabled="foreignFlg === 1"></v-radio>
      </v-radio-group>

      <span v-if="foreignFlg === 1">
        ご利用は有効な日本の免許証が必要となります<br />
        Valid Japanese Driver's License required.<br />
        <br />
      </span>

      <!-- 国籍名 -->
      <v-text-field
        v-show="isCountryName"
        label="国名"
        v-model="form.countryName"
      />

      <!-- 免許交付日 -->
      <c-date-field
        label="免許交付日"
        v-model="form.issue"
        :min-year="rangeIssue.min"
        :max-year="rangeIssue.max"
        :rules="[rule.required('免許交付日'), rule.date, rule.licenseAcquire]"
      />

      <!-- 有効期限 -->
      <c-date-field
        v-show="isLicense"
        label="有効期限"
        v-model="form.expire"
        :min-year="rangeExpire.min"
        :max-year="rangeExpire.max"
        :rules="[rule.date, rule.licenseExpire]"
      />

      <!-- 免許証番号 -->
      <v-text-field
        v-show="isLicense"
        label="免許証番号"
        :placeholder="'\u3000'"
        v-model="form.number"
        :rules="[rule.licenseNo]"
      />

      <!-- 免許取得日 -->
      <c-date-field
        v-show="isLicense"
        label="免許取得日"
        v-model="form.acquire"
        :min-year="rangeAcquire.min"
        :max-year="rangeAcquire.max"
        :rules="[rule.date, rule.licenseAcquire]"
      />

      <!-- 免許画像サンプル -->
      <img class="licenseImg" :src="licenseImg" v-show="isLicense" />

      <!-- 免許の種類 -->
      <c-license-type-select
        label="免許の種類"
        v-model="form.types"
        :rules="[rule.licenseTypes]"
      />
    </v-card-text>

    <c-card-errors :errors="errors"/>

    <!-- Actions -->
    <v-card-actions>
      <v-spacer/>

      <!-- template v-if="demoMode">
        <v-btn
          text outlined
          @click="onClearData"
          v-text="'クリア'"
        />
        <v-btn
          text outlined
          @click="onFillTestData"
          v-text="'テスト'"
        />
      </template -->

      <v-btn
        color="primary" type="submit"
        :disabled="!valid"
      >
        <slot name="submit-text"/>
      </v-btn>
    </v-card-actions>
  </c-form>
</template>

<style lang="scss">
  .licenseImg {
    width: 80%;
  }
</style>

<script>
import CForm from '@/components/CForm'
import CCardErrors from '@/components/CCardErrors'
import CDateField from '@/components/CDateField'
import CLicenseTypeSelect from '@/components/CLicenseTypeSelect'

import deepmerge from 'deepmerge'
import * as C from '@/consts'

import licenseImg from '@/assets/license.jpg'

export default {
  name: 'CLicenseGroup',
  components: {
    CForm,
    CCardErrors,
    CDateField,
    CLicenseTypeSelect
  },
  props: {
    license: {
      type: Object,
      required: true
    },
    errors: {
      type: [Array, String],
      default: ''
    }
  },
  data: () => ({
    // フォームデータ
    form: {
      number: '',
      acquire: '',
      expire: '',
      types: '',
      note: '',
      issue: '',
      country: 0,
      countryName: ''
    },

    rangeExpire: {
      min: 0,
      max: 0
    },

    rangeAcquire: {
      min: 0,
      max: 0
    },

    licenseImg,

    options: [
      { code: 1, text: '日本' },
      { code: 2, text: '外国籍' }
    ],

    // 国名入力
    isCountryName: false,

    // 免許情報入力
    isLicense: true,

    // デモデータ挿入する/しない
    demoMode: !!process.env.VUE_APP_DEMO_REGISTER,

    // フォームのバリデーション状態
    valid: true

    // フォームのエラーメッセージ
    // errors: []

    // 処理中
    // running: false
  }),

  created () {
    // license プロパティから form にデータをコピーする
    this.form = deepmerge(this.license, {})

    // 有効期限, 取得日の年の範囲を設定する
    const thisYear = new Date().getFullYear()

    // 有効期限の年: 今年〜6年後. ゴールド免許の有効期限 5 年 + 1年
    this.rangeExpire = { min: thisYear, max: thisYear + 6 }

    // 取得日の年: C.AGE_MAX 歳の人が 18 歳の免許取得した場合 - 1年
    this.rangeAcquire = { min: thisYear - (C.AGE_MAX - 17), max: thisYear }

    // 交付日の年:  - 5年 - 1年
    this.rangeIssue = { min: thisYear - 6, max: thisYear }

    // 国名の初期表示
    this.onChangeCountry()
  },

  computed: {
    // 外国人の取り扱い
    foreignFlg (store) {
      const foreign = store.getters['org/foreignFlg']
      return foreign
    }
  },

  methods: {
    /**
     * SUbmit ボタンが押された
     */
    onSubmit () {
      // エラー表示を消す
      this.errors = []

      if (this.form.country === 2 && this.foreignFlg === 1) {
        alert('ご利用は日本籍の方のみとなります')
        return
      }

      // フォーム入力されたことを通知する
      // this.running = true
      this.$emit('submit', this.form, errors => {
        this.running = false
        if (errors) {
          this.errors = errors
        }
      })
    },

    onChangeCountry () {
      if (this.form.country === 2) {
        if (this.foreignFlg === 1) {
          alert('ご利用は日本籍の方のみとなります')
          this.form.country = 1
          return
        }
        this.isCountryName = true
        this.isLicense = false
      } else {
        this.isCountryName = false
        this.isLicense = true
      }
    },

    onClearData () {
      // license プロパティから form にデータをコピーする
      this.form = deepmerge(this.license, {})
    },

    onFillTestData () {
      this.form.number = '990010011010'
      this.form.acquire = '2020-01-01'
      this.form.expire = '2023-05-01'
      this.form.types = '00010000000000100'
      this.form.note = ''
    }
  }
}
</script>
